import {
	TextField,
	Button,
	MenuItem,
	Select,
	InputLabel,
	FormControl,
	Box,
	Typography,
	CircularProgress,
	Alert
} from '@mui/material'
import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { UpdateUserPayload } from '../models/user'
import { useUser } from '../hooks/useUser'
import useRole from '../hooks/useRole'

export const EditUser = () => {
	const { state } = useLocation()
	const navigate = useNavigate()
	const { userId } = state as { userId: string }
	const { canWrite } = useRole()

	const { selectedUser, loadingUser, updating, error, fetchUser, updateUser } = useUser()

	// Deshabilitar formulario para usuarios sin permisos de escritura
	const isFormDisabled = !canWrite()

	const {
		control,
		handleSubmit,
		reset,
		formState: { isDirty }
	} = useForm<UpdateUserPayload & { photo: FileList }>()

	// Cargar usuario al montar
	useEffect(() => {
		if (userId) fetchUser(userId)
	}, [userId, fetchUser])

	// Cuando cambia selectedUser, resetear form
	useEffect(() => {
		if (selectedUser) {
			reset({
				firstname: selectedUser.firstname,
				lastname: selectedUser.lastname,
				email: selectedUser.email,
				role: selectedUser.role,
				position: selectedUser.position,
				photo: undefined
			})
		}
	}, [selectedUser, reset])

	// Helper para convertir File a base64
	const toBase64 = (file: File): Promise<string> =>
		new Promise((resolve, reject) => {
			const reader = new FileReader()
			reader.readAsDataURL(file)
			reader.onload = () => {
				const dataUrl = reader.result as string
				resolve(dataUrl.split(',')[1])
			}
			reader.onerror = (error) => reject(error)
		})

	const onSubmit = async (data: UpdateUserPayload & { photo: FileList }) => {
		if (!selectedUser) return

		// Construir payload solo con campos modificados
		const payload: UpdateUserPayload = {}

		if (data.firstname && data.firstname !== selectedUser.firstname) {
			payload.firstname = data.firstname
		}
		if (data.lastname && data.lastname !== selectedUser.lastname) {
			payload.lastname = data.lastname
		}
		if (data.email && data.email !== selectedUser.email) {
			payload.email = data.email
		}
		if (data.position && data.position !== selectedUser.position) {
			payload.position = data.position
		}
		if (data.role && data.role !== selectedUser.role) {
			payload.role = data.role
		}
		if (data.password) {
			payload.password = data.password
		}

		// Imagen solo si hay un archivo nuevo
		if (data.photo && data.photo.length > 0) {
			const file = data.photo[0]
			const ext = file.name.split('.').pop()?.toLowerCase()
			const base64 = await toBase64(file)
			payload.image = { ext: ext ?? undefined, data: base64 }
		}

		// Si no hay cambios, no llamar API
		if (Object.keys(payload).length === 0) {
			alert('Modifique al menos un campo para actualizar el usuario.')
		}

		if (await updateUser(userId, payload)) {
			alert('Usuario actualizado correctamente')
			navigate('/home/administrar-usuarios')
		}
	}

	if (loadingUser || !selectedUser) {
		return (
			<Box display="flex" justifyContent="center" mt={10}>
				<CircularProgress />
			</Box>
		)
	}

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 2,
				maxWidth: 750,
				margin: 'auto',
				p: 2,
				boxShadow: 3,
				borderRadius: 2,
				bgcolor: 'background.paper',
				mt: 10
			}}
			component="form"
			onSubmit={handleSubmit(onSubmit)}
		>
			{!canWrite() && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					No tienes permisos para editar usuarios. Solo puedes ver la información.
				</Alert>
			)}
			
			<Typography variant="h5" component="h1" align="center">
				Editar Usuario
			</Typography>

			{error && <Alert severity="error">{error}</Alert>}

			<Controller
				name="firstname"
				control={control}
				defaultValue=""
				render={({ field }) => (
					<TextField {...field} label="Nombres" variant="outlined" fullWidth disabled={isFormDisabled} />
				)}
			/>

			<Controller
				name="lastname"
				control={control}
				defaultValue=""
				render={({ field }) => (
					<TextField {...field} label="Apellidos" variant="outlined" fullWidth disabled={isFormDisabled} />
				)}
			/>

			<Controller
				name="email"
				control={control}
				defaultValue=""
				render={({ field }) => (
					<TextField
						{...field}
						label="Correo Electrónico"
						type="email"
						variant="outlined"
						fullWidth
						disabled={isFormDisabled}
					/>
				)}
			/>

			<Controller
				name="position"
				control={control}
				defaultValue=""
				render={({ field }) => (
					<TextField {...field} label="Posición" variant="outlined" fullWidth disabled={isFormDisabled} />
				)}
			/>

			<Controller
				name="password"
				control={control}
				defaultValue=""
				render={({ field }) => (
					<TextField {...field} label="Contraseña" type="password" variant="outlined" fullWidth disabled={isFormDisabled} />
				)}
			/>

			<Controller
				name="role"
				control={control}
				defaultValue=""
				render={({ field }) => (
					<FormControl fullWidth disabled={isFormDisabled}>
						<InputLabel id="role-label">Role</InputLabel>
						<Select {...field} labelId="role-label" label="Role">
							<MenuItem value="ADMIN_ROLE">Administrador</MenuItem>
							<MenuItem value="AUDITOR_ROLE">Auditor</MenuItem>
							<MenuItem value="USER_ROLE">Usuario</MenuItem>
						</Select>
					</FormControl>
				)}
			/>

			<Controller
				name="photo"
				control={control}
				defaultValue={undefined}
				render={({ field }) => (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						<TextField
							label="Fotografía"
							variant="outlined"
							fullWidth
							value={field.value && field.value.length ? (field.value[0] as File).name : ''}
							disabled
						/>
						<Button variant="contained" component="label" disabled={isFormDisabled}>
							Subir Archivo
							<input
								hidden
								accept="image/*"
								type="file"
								onChange={(e) => field.onChange(e.target.files)}
							/>
						</Button>
					</Box>
				)}
			/>
			<Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
				Tamaño máximo para imágenes: 100MB
			</Typography>

			<Button
				type="submit"
				variant="contained"
				color="primary"
				size="large"
				fullWidth
				disabled={!isDirty || updating || isFormDisabled}
			>
				{updating ? <CircularProgress size={24} /> : 'Guardar Cambios'}
			</Button>
		</Box>
	)
}
